import { useCallback, useMemo, useRef, useState } from "react";
import {
  getFieldErrorProps,
  runValidators,
  validateForm,
} from "../utils/formValidation";

/**
 * Reusable form validation hook.
 *
 * @param {object} initialValues
 * @param {object} schema - { field: [validator, ...] }
 *
 * @example
 * const form = useFormValidation(
 *   { email: "", password: "" },
 *   { email: [required(), email()], password: [required(), minLength(8)] }
 * );
 */
export default function useFormValidation(initialValues = {}, schema = {}) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;

  const reset = useCallback((nextValues) => {
    setValues(nextValues ?? initialValuesRef.current);
    setErrors({});
    setTouched({});
    setSubmitAttempted(false);
  }, []);

  const setFieldValue = useCallback((name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    const nextValue = type === "checkbox" ? checked : value;
    setFieldValue(name, nextValue);
  }, [setFieldValue]);

  const validateField = useCallback(
    (name, nextValues = values) => {
      const message = runValidators(
        nextValues[name],
        schema[name] || [],
        nextValues
      );
      setErrors((prev) => {
        if (!message && !prev[name]) return prev;
        if (!message) {
          const next = { ...prev };
          delete next[name];
          return next;
        }
        return { ...prev, [name]: message };
      });
      return message;
    },
    [schema, values]
  );

  const handleBlur = useCallback(
    (event) => {
      const { name } = event.target;
      setTouched((prev) => ({ ...prev, [name]: true }));
      validateField(name);
    },
    [validateField]
  );

  const validateAll = useCallback(() => {
    setSubmitAttempted(true);
    const fieldNames = Object.keys(schema);
    const nextTouched = fieldNames.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched((prev) => ({ ...prev, ...nextTouched }));

    const result = validateForm(values, schema);
    setErrors(result.errors);
    return result;
  }, [schema, values]);

  const getFieldProps = useCallback(
    (name, options = {}) => {
      const { helperText = "", type } = options;
      const errorProps = getFieldErrorProps(
        errors,
        { ...touched, ...(submitAttempted ? { [name]: true } : {}) },
        name,
        helperText
      );

      return {
        name,
        value: values[name] ?? "",
        onChange: handleChange,
        onBlur: handleBlur,
        ...(type ? { type } : {}),
        ...errorProps,
      };
    },
    [errors, handleBlur, handleChange, submitAttempted, touched, values]
  );

  const isValid = useMemo(
    () => validateForm(values, schema).isValid,
    [schema, values]
  );

  return {
    values,
    errors,
    touched,
    submitAttempted,
    isValid,
    setValues,
    setFieldValue,
    setErrors,
    handleChange,
    handleBlur,
    validateField,
    validateAll,
    getFieldProps,
    reset,
  };
}
